import { toSignificant, toPercentage } from '@simplywallst/formatting';
import DataTable from '@components/DataTable';
import { RawDataTimeSeries } from '@/components/RawData';
import { SWS_ANALYSIS_MODEL } from '@/constants';

export interface Props {
  uniqueSymbol: string;
  reportingCurrencyISO: string;
  analystCount: number;
  industryCountryName: string;
  industrySecondaryName: string;
  industryAverageDividendYield: number;
  industryAverageDividendYieldCount: number;
  marketCountryName: string;
  marketDividendYield: number;
  marketDividendYieldCount: number;
  marketDividendYield10thPercentile: number;
  marketDividendYield25thPercentile: number;
  marketDividendYield75thPercentile: number;
  futureDividendEstimates: { dateKey: number; data: number[] }[];
  pastAnnualizedDividendPayouts: {
    dateKey: number;
    data: number[];
  }[];
}

const futureDividendEstimateHeaders = [
  'Dividend per Share (annual)',
  'Avg. No. Analysts',
];

const pastDividendsHeaders = ['Dividend per share (annual)', 'Avg. Yield (%)'];

const StabilityAndGrowthOfPaymentsRawData = ({
  uniqueSymbol,
  reportingCurrencyISO,
  analystCount,
  industryCountryName,
  industrySecondaryName,
  industryAverageDividendYield,
  industryAverageDividendYieldCount,
  marketCountryName,
  marketDividendYield,
  marketDividendYieldCount,
  marketDividendYield10thPercentile,
  marketDividendYield25thPercentile,
  marketDividendYield75thPercentile,
  futureDividendEstimates,
  pastAnnualizedDividendPayouts,
}: Props) => (
  <div>
    <DataTable columnWidths="5, 4, 1" light>
      <caption>{`${uniqueSymbol} Annualized Past and Future Dividends`}</caption>
      <thead>
        <tr>
          <th>Data Point</th>
          <th>Source</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Past Annualized Dividend Yield</td>
          <td>S&P Global Market Data</td>
          <td>See Below</td>
        </tr>
        <tr>
          <td>Past Dividends per Share</td>
          <td>Company Filings/ Annualized Dividend Payments</td>
          <td>See Below</td>
        </tr>
        <tr>
          <td>Future Dividends per Share Estimates</td>
          <td>
            {`Up to ${toSignificant(
              analystCount,
              0
            )} Analyst Estimates on Average (S&P Global)`}
          </td>
          <td>See Below</td>
        </tr>
        <tr>
          <td>
            {`${industryCountryName} ${industrySecondaryName} Industry Average Dividend Yield`}
          </td>
          <td>
            {`Market Cap Weighted Average of ${industryAverageDividendYieldCount.toLocaleString()} Stocks`}
          </td>
          <td>{toPercentage(industryAverageDividendYield, 1)}</td>
        </tr>
        <tr>
          <td>{`${marketCountryName} Market Average Dividend Yield`}</td>
          <td>
            {`Market Cap Weighted Average of ${marketDividendYieldCount.toLocaleString()} Stocks`}
          </td>
          <td>{toPercentage(marketDividendYield, 1)}</td>
        </tr>
        <tr>
          <td>{`${marketCountryName} Minimum Threshold Dividend Yield`}</td>
          <td>10th Percentile</td>
          <td>{toPercentage(marketDividendYield10thPercentile, 1)}</td>
        </tr>
        <tr>
          <td>{`${marketCountryName} Bottom 25% Dividend Yield`}</td>
          <td>25th Percentile</td>
          <td>{toPercentage(marketDividendYield25thPercentile, 1)}</td>
        </tr>
        <tr>
          <td>{`${marketCountryName} Top 25% Dividend Yield`}</td>
          <td>75th Percentile</td>
          <td>{toPercentage(marketDividendYield75thPercentile, 1)}</td>
        </tr>
      </tbody>
    </DataTable>
    <p>
      <a
        href={`${SWS_ANALYSIS_MODEL}/industry_averages.markdown`}
        data-unique-symbol={uniqueSymbol}
        data-about-section="annual_past_future_dividends"
        data-link-name="model_industry_averages"
        target="_blank"
        rel="noreferrer noopener nofollow"
      >
        Industry and Market average data
      </a>
      <span> is calculated daily.</span>
    </p>
    <p>
      Note all dividend per share amounts are annualized and not quarterly or
      other period.
    </p>
    <RawDataTimeSeries
      uniqueSymbol={uniqueSymbol}
      title="Future Dividends Estimate Data"
      reportingCurrencyISO={reportingCurrencyISO}
      numberPrecision={2}
      tableHeaderDescriptions={futureDividendEstimateHeaders}
      tableData={futureDividendEstimates}
    />
    <p> </p>
    <RawDataTimeSeries
      uniqueSymbol={uniqueSymbol}
      title="Past Annualized Dividends Data"
      reportingCurrencyISO={reportingCurrencyISO}
      numberPrecision={3}
      tableHeaderDescriptions={pastDividendsHeaders}
      tableData={pastAnnualizedDividendPayouts}
    />
    <p>
      <span>
        {`Learn more about our ratios and growth rates in Simply Wall St’s `}
      </span>
      <a
        href={`${SWS_ANALYSIS_MODEL}/MODEL.markdown`}
        data-unique-symbol={uniqueSymbol}
        data-about-section="annual_past_future_dividends"
        data-link-name="model_growth_rates"
        target="_blank"
        rel="noreferrer noopener nofollow"
      >
        analysis model.
      </a>
    </p>
  </div>
);

export default StabilityAndGrowthOfPaymentsRawData;
